import { Flex } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useDebounce } from 'react-use'
import { useModalState } from '~/atoms/ModalState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { PhoneStatus } from '~/atoms/WebphoneState'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useNetworkConnectivity } from '~/hooks/useNetworkConnectivity'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { SettingsType } from '~/types/Settings'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { formatPhoneNumber, getValidNumber } from '~/utils/formatter'

import { cleanDialstring } from '@mangovoice/core'
import { ResourceLoadingState } from '@mangovoice/types'

import {
  ContactDetails,
  ContactNumber,
  useContactsViewModel,
} from '../Contacts/viewModels/Contacts.viewmodel'
import { useRecentCalls } from './atoms/RecentCalls.atom'
import { CallErrorView } from './CallErrorView'
import { phoneColorKeys } from './componentStyles/_colors'
import { ExtensionSelector } from './ExtensionSelector'
import { InCallView } from './InCallView'
import { OutOfCallView } from './OutOfCallView'
import { PhoneInitializingView } from './PhoneInitializingView'
import { QuickDialView } from './QuickDialView'

const { WebPhonePage: PageColors } = phoneColorKeys.Phone

const tabStateAtom = atomWithStorage<'dialpad' | 'search'>(
  StorageKeys.WebphoneTabState,
  'dialpad',
)

const DEBOUNCE_WAIT_MS = 250

const WebPhoneView = () => {
  const [tabState, setTabState] = useAtom(tabStateAtom)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [debounceSearchValue, setDebounceSearchValue] = useState(
    SearchValue.empty,
  )
  const cancelSearchRef = useRef<(() => void) | undefined>()

  const [dtmfHistory, setDtmfHistory] = useState<string>('')
  const [autoDialNumber, setAutoDialNumber] = useState<string | undefined>(
    undefined,
  )
  const { pbxId } = useUserState()
  const {
    callModal: [, setCallModalState],
    isAnyModalOpen,
  } = useModalState()

  useTogglePanel()

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        conference: {
          conferencedCalls,
          startTime: conferenceStartTime,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
        },
        list: { callList },
        isAtMaxCallLimit,
      },
      phoneStatus,
      registrationError,
      extensions,
      trackedExtensions,
      trackedParkingLots,
      audioDevices: {
        output: { devices: outputs, preference: selectedOutputDeviceId },
        input: { devices: inputs, preference: selectedInputDeviceId },
      },
      speedDial,
    },
    actions: {
      resetConnection,
      startCall,
      muteCall,
      holdCall,
      transferCall,
      parkCall,
      endCall,
      sendDtmf,
      switchAudioInput,
      switchAudioOutput,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleHoldConference,
      toggleMuteConference,
    },
  } = usePhoneEndpoint({
    onCallEnd: () => {
      setDebounceSearchValue(SearchValue.empty)
      setPhoneNumber('')
      setDtmfHistory('')
    },
  })

  const { recentContacts } = useRecentCalls(pbxId, extensions)

  const {
    state: {
      searchValue: contactsSearchValue,
      contactsListState: { data: contactsList },
      contactsListLoadingState,
      contactSources,
      contactSourcesLoadingState,
      selectedContactSourceId,
    },
    actions: {
      setSearchValue,
      setSelectedContactSource,
      getNextContactsPage,
      getContactDetails,
      getContactSources,
    },
  } = useContactsViewModel()

  useEffect(() => {
    if (
      !contactSources &&
      contactSourcesLoadingState?.state !== ResourceLoadingState.Loading
    ) {
      getContactSources()
    }
    if (selectedContactSourceId) {
      setSelectedContactSource(undefined, false)
    }
  })

  const isSearchLoading =
    !contactsListLoadingState?.state ||
    contactsListLoadingState.state === ResourceLoadingState.Loading
  const searchResults = contactsList.map(getContactDetails)

  const location = useLocation()
  const navigate = useNavigate()
  const [queryParams] = useSearchParams()
  const number = queryParams.get('number')
  const autoDial = queryParams.get('autoDial')
  const isOnline = useNetworkConnectivity({
    playConnectionChangeSound: false,
    reportAnalytics: false,
  })

  //only recompute route number if the route hash or number changes
  useEffect(() => {
    if (
      number &&
      (phoneNumber === '' || phoneNumber !== number) &&
      !autoDialNumber
    ) {
      setTabState('dialpad')
      setPhoneNumber(number)

      if (autoDial) {
        setAutoDialNumber(number)
      }

      navigate(ApplicationRoutes.WEB_PHONE, {
        replace: true,
      })
    }
  }, [location.hash, number])

  useDebounce(
    () => {
      cancelSearchRef.current = setSearchValue(debounceSearchValue, true)
    },
    DEBOUNCE_WAIT_MS,
    [debounceSearchValue],
  )

  function handleSearchTermChanged(searchValue: SearchValue) {
    if (cancelSearchRef.current) {
      cancelSearchRef.current()
      cancelSearchRef.current = undefined
    }
    setDebounceSearchValue(searchValue)
  }

  const handleScrollEndReached = () => {
    getNextContactsPage()
  }

  const handlePhoneNumberChanged = (newNumber: string) => {
    setPhoneNumber(newNumber)
  }

  const handleDialButtonClick = (digit: string) => {
    setPhoneNumber((prev) => prev + digit)
  }

  const handleDeleteButtonClick = () => {
    setPhoneNumber((prev) => getValidNumber(prev).slice(0, -1))
  }

  const handleDtmfClick = (digit: string) => {
    if (!activeCall) return
    sendDtmf(activeCall.callUuid, digit)
    setDtmfHistory(() => dtmfHistory + digit)
    track(WebPhoneAnalyticsEvents.SendDtmf)
  }

  const handlePhoneSettingsLinkClick = () => {
    navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.PHONE}`)
  }

  const handleNewCallWithTargetClick = (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => {
    setCallModalState({ open: true, callee })
  }
  const handleNewCallClick = () => {
    setCallModalState({ open: true })
  }

  const handleStartCall = (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => {
    console.log(
      'WebPhoneView handleStartCall outboundCallerIdName:',
      outboundCallerIdName,
    )
    if (typeof callee === 'string') {
      startCall(cleanDialstring(callee), outboundCallerIdName)
    } else {
      startCall(callee, outboundCallerIdName)
    }
    track(WebPhoneAnalyticsEvents.StartCallFromPage)
  }

  const handleMergeCalls = () => {
    const callUuids = Array.from(callList.keys())
    mergeCalls(callUuids)
  }
  const handleSplitCalls = () => {
    splitCalls()
  }

  const handleEndCall = () => {
    if (conferencedCalls) {
      endConferenceCall()
      return
    }
    endCall(activeCall?.callUuid ?? '')
  }

  const handleTransferCall = (transferTarget: string) => {
    if (!activeCall) return
    transferCall(activeCall.callUuid, transferTarget)
  }

  const handleParkCall = (parkTarget: string) => {
    if (!activeCall) return
    parkCall(activeCall.callUuid, parkTarget)
  }

  const handleMuteClick = () => {
    if (conferencedCalls) {
      toggleMuteConference()
      return
    }
    if (!activeCall) return
    muteCall(activeCall.callUuid)
  }

  const handleHoldClick = async () => {
    if (conferencedCalls) {
      await toggleHoldConference()
      return
    }
    if (!activeCall) return
    await holdCall(activeCall.callUuid)
  }

  const handleInputDeviceChange = (deviceId: string) => {
    switchAudioInput(deviceId)
  }

  const handleOutputDeviceChange = (deviceId: string) => {
    switchAudioOutput(deviceId)
  }

  const handleRetry = () => {
    resetConnection()
  }

  const isConnected =
    phoneStatus !== PhoneStatus.TRANSPORT_DISCONNECTED &&
    phoneStatus !== PhoneStatus.ERROR

  if (autoDialNumber && phoneStatus === PhoneStatus.READY && !activeCall) {
    setAutoDialNumber(undefined)
    startCall(autoDialNumber)
  }

  const callerDisplayNames = useMemo(() => {
    if (conferencedCalls) {
      return conferencedCalls.map((call) => {
        if (call.name && call.name !== '') {
          return call.name
        } else {
          return formatPhoneNumber(call.number)
        }
      })
    }
    if (!activeCall) return ['Unknown']
    if (activeCall.name && activeCall.name !== '') {
      return [activeCall.name]
    } else {
      return [formatPhoneNumber(activeCall.number)]
    }
  }, [activeCall, conferencedCalls])

  const getPhoneDetailView = () => {
    const isInitializing = phoneStatus === PhoneStatus.INITIAL

    const isErrorState =
      phoneStatus === PhoneStatus.ERROR ||
      phoneStatus === PhoneStatus.TRANSPORT_DISCONNECTED ||
      phoneStatus === PhoneStatus.MIC_DENIED

    const isOutOfCall = phoneStatus === PhoneStatus.READY

    const isInCall =
      activeCall &&
      (phoneStatus === PhoneStatus.CALL_CONNECTING ||
        phoneStatus === PhoneStatus.RINGING ||
        phoneStatus === PhoneStatus.CALL_ACTIVE)

    let detailView: ReactNode
    if (isInitializing) {
      detailView = <PhoneInitializingView onRetryClick={handleRetry} />
    }
    if (isOutOfCall) {
      detailView = (
        <OutOfCallView
          activeTab={tabState}
          onChangeTab={setTabState}
          phoneNumber={phoneNumber}
          searchValue={debounceSearchValue}
          searchedValue={contactsSearchValue}
          isSearching={hasValue(contactsSearchValue?.value)}
          isInputDisabled={isAnyModalOpen}
          onSearchTermChange={handleSearchTermChanged}
          searchResults={searchResults}
          onResultsEndReached={handleScrollEndReached}
          isSearchLoading={isSearchLoading}
          onStartCall={handleStartCall}
          onDialButtonClick={handleDialButtonClick}
          onDeleteButtonClick={handleDeleteButtonClick}
          onPhoneNumberChange={handlePhoneNumberChanged}
        />
      )
    }
    if (isInCall) {
      const isInConference =
        conferencedCalls !== undefined && conferencedCalls.length > 1

      const isOnHold = isInConference ? conferenceIsOnHold : activeCall.isOnHold
      const isMuted = isInConference ? conferenceIsMuted : activeCall.isMuted

      const startTime = isInConference
        ? conferenceStartTime
        : activeCall.startTime

      const phonenumber = isInConference ? undefined : activeCall.number

      detailView = (
        <InCallView
          callerNames={callerDisplayNames}
          phonenumber={phonenumber}
          dtmfHistory={dtmfHistory}
          callStartTime={startTime}
          isOnline={isOnline}
          isOnHold={isOnHold}
          callStatus={phoneStatus}
          isMuted={isMuted}
          onMuteClick={handleMuteClick}
          onHoldClick={handleHoldClick}
          onParkCall={handleParkCall}
          onTransferCall={handleTransferCall}
          pbxExtensions={extensions}
          parkingLots={trackedParkingLots}
          onDialButtonClick={handleDtmfClick}
          audioInputs={inputs}
          selectedInput={selectedInputDeviceId}
          onInputDeviceChange={handleInputDeviceChange}
          audioOutputs={outputs}
          selectedOutput={selectedOutputDeviceId}
          onOutputDeviceChange={handleOutputDeviceChange}
          onCallStop={handleEndCall}
          isBelowMaxCallLimit={!isAtMaxCallLimit}
          isInConference={isInConference}
          onNewCall={handleNewCallClick}
          onMergeCalls={handleMergeCalls}
          onSplitConference={handleSplitCalls}
        />
      )
    }
    if (isErrorState) {
      detailView = (
        <CallErrorView
          error={registrationError}
          isMicPermissionDenied={phoneStatus === PhoneStatus.MIC_DENIED}
          isConnected={isConnected}
          onRetryClick={handleRetry}
        />
      )
    }

    return (
      <Flex direction="column" boxSize="full" p="0.25rem">
        <Flex
          direction="column"
          boxSize="full"
          borderRadius="0.5rem"
          border="2px solid"
          borderColor={PageColors.Border}
          bg={PageColors.Background}
        >
          <ExtensionSelector
            hasActiveCall={!!activeCall}
            layoutProps={{
              h: { base: '3.75rem', lg: '5rem' },
            }}
          />
          {detailView}
        </Flex>
      </Flex>
    )
  }

  return (
    <ResponsivePage
      detailPanel={getPhoneDetailView}
      listPanel={
        <QuickDialView
          hasActiveCall={activeCall !== undefined}
          isAtMaxCallLimit={isAtMaxCallLimit}
          recentlyDialed={recentContacts}
          parkingLots={trackedParkingLots}
          extensions={trackedExtensions}
          speedDial={speedDial}
          onPark={handleParkCall}
          onTransfer={handleTransferCall}
          onStartCall={handleStartCall}
          onNewCall={handleNewCallWithTargetClick}
          onParkSettingsLinkClick={handlePhoneSettingsLinkClick}
        />
      }
    />
  )
}

export default WebPhoneView
